import { SideNav, SideNavContent } from "components";
import { HomeWrapper } from "components/CdBalance/style";
import {
  BaseDropdown,
  BaseInput,
  LargeButton,
  PageTitle,
  Ptag,
  OverlayLoader,
} from "components/core";
import { Form, Formik, FieldArray } from "formik";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  generateInitialValues,
  postCompanyData,
  validationSchema,
} from "./constant";
import http from "services/httpService";
import { Divider, IconButton } from "@mui/material";
import closeIcon from "assets/svgs/close.svg";
import add_circle from "assets/svgs/add_circle.svg";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import styled from "styled-components";
import { WidthLimiter } from "pages/PlanSetup/styles";
import { useNavigate, useParams } from "react-router";
import routes from "components/routes";
import { colors } from "theme";
const BaseDivider = () => {
  return (
    <Divider
      style={{ marginTop: "5rem", marginBottom: "5rem", borderColor: "black" }}
    />
  );
};

// Helper function to check if all required fields are filled
const checkRequiredFields = (values) => {
  const isTenDigitNumber = (val) => /^\d{10}$/.test(String(val || "").trim());
  const requiredFields = [
    'company_name',
    'range_of_employees',
    'industry',
    'industry_sector',
    'street',
    'city',
    'pincode',
    'state',
    'primary_contact_name',
    'email',
    'phone',
    'servicing_managers',
    'relationship_managers'
  ];

  const baseFieldsValid = requiredFields.every(field => {
    const value = values[field];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value && value.toString().trim() !== '';
  });

  if (!baseFieldsValid) return false;

  if (!isTenDigitNumber(values?.phone)) return false;

  const secondaryContacts = values?.secondary_contacts || [];
  if (!Array.isArray(secondaryContacts) || secondaryContacts.length === 0) return true;

  return secondaryContacts.every((contact) => {
    const name = contact?.name;
    const email = contact?.email;
    const phone = contact?.phone_no;
    return (
      name && name.toString().trim() !== '' &&
      email && email.toString().trim() !== '' &&
      phone && phone.toString().trim() !== '' &&
      isTenDigitNumber(phone)
    );
  });
};

// Custom hook for form persistence
const useFormPersistence = (storageKey, formValues, enabled = true) => {
  const timeoutRef = useRef(null);

  // Debounced save function to avoid excessive localStorage writes
  const debouncedSave = useCallback((values) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (enabled && values && Object.keys(values).length > 0) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(values));

        } catch (error) {
          console.warn('Failed to save form data to localStorage:', error);
        }
      }
    }, 500); // Save after 500ms of inactivity
  }, [storageKey, enabled]);

  // Load saved data
  const loadSavedData = useCallback(() => {
    if (!enabled) return null;

    try {
      const savedData = localStorage.getItem(storageKey);
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
      console.warn('Failed to load saved form data:', error);
      return null;
    }
  }, [storageKey, enabled]);

  // Clear saved data
  const clearSavedData = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear saved form data:', error);
    }
  }, [storageKey]);

  // Auto-save when form values change
  useEffect(() => {
    if (formValues) {
      debouncedSave(formValues);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formValues, debouncedSave]);

  return { loadSavedData, clearSavedData };
};

// Separate component to handle form content and avoid hooks inside callbacks
const FormContent = ({
  formikProps,
  onValuesChange,
  industries,
  employeerange,
  industrySector,
  relationshipManager,
  servicingManager,
  getOptions,
  getManagerOptions,
  type,
  dataExist // Added dataExist prop
}) => {
  // This useEffect is now properly at the top level of a React component
  useEffect(() => {
    onValuesChange(formikProps.values);
  }, [formikProps.values, onValuesChange]);

  // Check if form should be submittable
  const isFormSubmittable = formikProps.isValid && checkRequiredFields(formikProps.values);

  return (
    <Form
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        marginTop: "2rem",
        maxWidth: "932px",
      }}
    >
      <BaseInput
        staticLabel="Company Name (Policy Holder)"
        fontSize="16px !important"
        formikProps={{
          ...formikProps,
          name: "company_name",
        }}
        onKeyDown={(e) => {
          // Allow: letters, numbers, spaces, hyphens, apostrophes, dots, commas, ampersands
          // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
          const allowedChars = /^[a-zA-Z0-9\s\-'.,&]$/;
          const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

          if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
            e.preventDefault();
          }
        }}
      />
      <BaseInput
        staticLabel="Parent Company (Optional)"
        fontSize="16px !important"
        formikProps={{
          ...formikProps,
          name: "parent_company_name",
        }}
        onKeyDown={(e) => {
          // Allow: letters, numbers, spaces, hyphens, apostrophes, dots, commas, ampersands
          // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
          const allowedChars = /^[a-zA-Z0-9\s\-'.,&]$/;
          const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

          if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
            e.preventDefault();
          }
        }}
      />
      <BaseDropdown
        fontSize="16"
        staticLabel="Number of Employees"
        type="text"
        options={getOptions(employeerange)}
        formikProps={{
          ...formikProps,
          name: "range_of_employees",
        }}
      />
      <BaseDropdown
        fontSize="16"
        staticLabel="Industry Type"
        type="text"
        options={getOptions(industries)}
        formikProps={{ ...formikProps, name: "industry" }}
      />
      <BaseDropdown
        fontSize="16"
        staticLabel="Industry Sector"
        type="text"
        options={getOptions(industrySector)}
        formikProps={{ ...formikProps, name: "industry_sector" }}
      />
      <BaseDivider />
      <Ptag
        fontSize="24px"
        letterSpacing="0.25px"
        fontWeight="700"
        fontFamily="Inter"
      >
        Address
      </Ptag>
      <BaseInput
        staticLabel="Area/ Street/ Sector"
        fontSize="16px !important"
        formikProps={{
          ...formikProps,
          name: "street",
        }}
        onKeyDown={(e) => {
          // Allow: letters, numbers, spaces, hyphens, slashes, dots, commas, and common special characters
          // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
          const allowedChars = new RegExp('^[a-zA-Z0-9\\s/.,\\-#&\'():@+]$');
          const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

          if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
            e.preventDefault();
          }
        }}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "68% 30%",
          gridGap: "2%",
        }}
      >
        <BaseInput
          staticLabel="City/ Town"
          fontSize="16px !important"
          formikProps={{
            ...formikProps,
            name: "city",
          }}
          onKeyDown={(e) => {
            // Allow: letters and spaces only
            // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
            const allowedChars = /^[a-zA-Z\s]$/;
            const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

            if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
        <BaseInput
          staticLabel="Pincode"
          pincode
          fontSize="16px !important"
          formikProps={{
            ...formikProps,
            name: "pincode",
          }}
          onKeyDown={(e) => {
            // Allow: numbers only
            // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
            const allowedChars = /^[0-9]$/;
            const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

            if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
      </div>
      <BaseInput
        staticLabel="State"
        fontSize="16px !important"
        formikProps={{
          ...formikProps,
          name: "state",
        }}
        onKeyDown={(e) => {
          // Allow: letters and spaces only
          // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
          const allowedChars = /^[a-zA-Z\s]$/;
          const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

          if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
            e.preventDefault();
          }
        }}
      />
      <BaseDivider />
         <Ptag
        fontSize="24px"
        fontWeight="700"
        fontFamily="Inter"
      >
        HR Contact Details
      </Ptag>
      <ContactCard>
     
        <ContactHeader>
          <ContactTitle>HR Admin</ContactTitle>
        </ContactHeader>
        <BaseInput
          staticLabel="Name"
          fontSize="16px !important"
          formikProps={{
            ...formikProps,
            name: "primary_contact_name",
          }}
          onKeyDown={(e) => {
            // Allow: letters, spaces, hyphens, apostrophes
            // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
            const allowedChars = /^[a-zA-Z\s\-']$/;
            const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

            if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
        <GridTwoColumns>
          <BaseInput
            staticLabel="Email"
            fontSize="16px !important"
            formikProps={{
              ...formikProps,
              name: "email",
            }}
          />
          <BaseInput
            staticLabel="Mobile"
            phone
            fontSize="18px !important"
            disabled={dataExist}
            formikProps={{
              ...formikProps,
              name: "phone",
            }}
            style={{
              opacity: dataExist ? 0.6 : 1,
              cursor: dataExist ? "not-allowed" : "default",
            }}
          />
        </GridTwoColumns>
      </ContactCard>

      <FieldArray name="secondary_contacts">
        {({ insert, remove, push }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {formikProps.values.secondary_contacts && formikProps.values.secondary_contacts.length > 0 && formikProps.values.secondary_contacts.map((contact, index) => (
              <ContactCard key={index} style={{ marginBottom: 0 }}>
                <ContactHeader>
                  <ContactTitle>HR User</ContactTitle>
                  <IconButton 
                    onClick={() => remove(index)}
                    size="small"
                    style={{ padding: 0 }}
                  >
                    <img src={closeIcon} alt="Close" style={{ width: 24, height: 24 }} />
                  </IconButton>
                </ContactHeader>
                <BaseInput
                  staticLabel="Name"
                  fontSize="18px !important"
                  formikProps={{
                    ...formikProps,
                    name: `secondary_contacts.${index}.name`,
                  }}
                  onKeyDown={(e) => {
                    // Allow: letters, spaces, hyphens, apostrophes
                    // Also allow editing keys: Backspace, Delete, Arrow keys, Tab, etc.
                    const allowedChars = /^[a-zA-Z\s\-']$/;
                    const editingKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

                    if (!allowedChars.test(e.key) && !editingKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
                <GridTwoColumns>
                  <BaseInput
                    staticLabel="Email"
                    fontSize="18px !important"
                    formikProps={{
                      ...formikProps,
                      name: `secondary_contacts.${index}.email`,
                    }}
                  />
                  <BaseInput
                    staticLabel="Mobile"
                    phone
                    fontSize="18px !important"
                    formikProps={{
                      ...formikProps,
                      name: `secondary_contacts.${index}.phone_no`,
                    }}
                  />
                </GridTwoColumns>
              </ContactCard>
            ))}
            
            <AddButton
              type="button"
              onClick={() => push({ name: '', email: '', phone_no: '' })}
            >
            <img src={add_circle} alt="Add" style={{marginLeft:'18px',height:20,width:20}} />
              <Ptag fontSize="14px" fontWeight="500" fontFamily="Inter" style={{marginLeft:'-5px'}}>
 Add HR User
              </Ptag>
             
            </AddButton>
          </div>
        )}
      </FieldArray>
      <BaseDivider />
      <Ptag
        fontSize="24px"
        fontWeight="700"
        fontFamily="Inter"
      >
        Point of Contact
      </Ptag>

      <BaseDropdown
        fontSize="16"
        multiple
        staticLabel="Servicing Manager"
        type="text"
        options={getManagerOptions(servicingManager)}
        formikProps={{
          ...formikProps,
          name: "servicing_managers",
        }}
      />
      <BaseDropdown
        fontSize="16"
        staticLabel="Relationship Manager"
        type="text"
        multiple
        options={getManagerOptions(relationshipManager)}
        formikProps={{
          ...formikProps,
          name: "relationship_managers",
        }}
      />
      <ButtonWrapper
        style={{
          marginTop: "4rem",
        }}
      >
        <LargeButton
          id=""
          label={`${type === "edit" ? "Update" : (dataExist ? "Proceed" : "Create Account")}`}
          type="submit"
          style={{
            width: "932px",
          }}
          disabled={!isFormSubmittable}
          opacity={!isFormSubmittable ? 0.6 : 1}
        />
      </ButtonWrapper>
    </Form>
  );
};

function CompanySetup() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [industries, setIndustries] = useState({});
  const [employeerange, setEmployeeRange] = useState({});
  const [industrySector, setIndustrySector] = useState({});
  const [relationshipManager, setRelationshipManager] = useState({});
  const [servicingManager, setServicingManager] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [dataExist, setDataExist] = useState(false);
  const [profileData, setProfileData] = useState();
  const [currentFormValues, setCurrentFormValues] = useState(null);
  const [showAutoSaveIndicator, setShowAutoSaveIndicator] = useState(false);

  // Storage key for persistence
  const storageKey = `company_setup_draft_${type || 'new'}`;

  // Use the persistence hook
  const { loadSavedData, clearSavedData } = useFormPersistence(
    storageKey,
    currentFormValues,
    true // Enable persistence
  );

  const getOptions = (details) => {
    if (!Object.keys(details).length) return [];
    return details.map(({ display_name, value }) => ({
      label: display_name,
      value,
    }));
  };

  const getManagerOptions = (details) => {
    if (!Object.keys(details).length) return [];
    return details.map(({ name, id }) => ({
      label: name,
      value: id,
    }));
  };

  const initialValues = useMemo(() => {
    // Priority: 1. Profile data (edit mode), 2. Existing company_setup_data, 3. Saved draft, 4. Default values
    let baseValues = generateInitialValues(profileData, type);

    // Check if there's existing completed company setup data
    let existingCompanyData = null;
    try {
      const existingData = localStorage.getItem("company_setup_data");
      existingCompanyData = existingData ? JSON.parse(existingData) : null;
    } catch (error) {
      console.warn('Failed to load existing company setup data:', error);
    }

    // If no profile data (new form or edit mode before profile loads), check for existing data first
    if (!profileData || Object.keys(profileData || {}).length === 0) {
      if (existingCompanyData && Object.keys(existingCompanyData).length > 0) {
        baseValues = { ...baseValues, ...existingCompanyData };
      } else {
        // Only use draft data if there's no existing completed data
        const savedData = loadSavedData();
        if (savedData) {
          console.log('Loaded saved draft form data');
          baseValues = { ...baseValues, ...savedData };
        }
      }
    }

    return baseValues;
  }, [profileData, type, loadSavedData]);

  // Check if data exists and set dataExist state
  useEffect(() => {
    const checkDataExists = () => {
      try {
        // Check if profile data exists (edit mode)
        if (profileData && Object.keys(profileData).length > 0) {
          setDataExist(true);
          return;
        }

        // Check if existing company setup data exists
        const existingData = localStorage.getItem("company_setup_data");
        if (existingData) {
          const parsedData = JSON.parse(existingData);
          if (parsedData && (parsedData.phone || parsedData.secondary_contact)) {
            setDataExist(true);
            return;
          }
        }

        // If we're in edit mode but haven't loaded profile data yet, assume data exists
        if (type === "edit") {
          setDataExist(true);
          return;
        }

        setDataExist(false);
      } catch (error) {
        console.warn('Error checking data existence:', error);
        setDataExist(false);
      }
    };

    checkDataExists();
  }, [profileData, type]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await http.get("business/get_company_setup_details/");
        setIndustries(data?.industry_type);
        setEmployeeRange(data?.range_of_employees);
        setIndustrySector(data?.industry_sector);
        setRelationshipManager(data?.relationship_managers);
        setServicingManager(data?.servicing_managers);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const companyProfile = async () => {
      try {
        const { data } = await http.get("/business/get_company_profile/");
        setProfileData(data);
      } catch (e) {
        console.log(e);
      }
    };

    fetchData();
    if (type === "edit") {
      companyProfile();
    }
  }, [type]);

  // Show auto-save indicator briefly when form is auto-saved
  useEffect(() => {
    if (currentFormValues) {
      setShowAutoSaveIndicator(true);
      const timer = setTimeout(() => {
        setShowAutoSaveIndicator(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentFormValues]);

  const handleSubmit = async (values) => {
    setLoader(true);
    try {
      const existingData = localStorage.getItem("company_setup_data");

      const result = await postCompanyData(values, existingData ? "edit" : type, "setup");
      if (result) {
        // Clear the draft data since form was successfully submitted
        clearSavedData();

        // Keep the final submitted data
        localStorage.setItem("company_setup_data", JSON.stringify(values));

        if (type === "edit") {
          navigate(`/${routes.CARE_DASHBOARD}`);
        } else {
          navigate(`/${routes.PLAN_SELECTION}`);
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoader(false);
    }
  };

  // Handle form values change for persistence
  const handleFormValuesChange = useCallback((values) => {
    setCurrentFormValues(values);
  }, []);

  // Clear draft data when user explicitly wants to start fresh
  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear all saved form data?')) {
      clearSavedData();
      window.location.reload(); // Reload to reset form
    }
  };

  return (
    <>
      <SideNav>{type === "edit" && <SideNavContent />}</SideNav>
      {loader ? (
        <OverlayLoader />
      ) : (
        <HomeWrapper>
          <div style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <PageTitle title="Company Information " />
          </div>

          <WidthLimiter>
            <Formik
              enableReinitialize
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {(formikProps) => (
                <div style={{ marginBottom: "2.8rem" }}>
                  <FormContent
                    formikProps={formikProps}
                    onValuesChange={handleFormValuesChange}
                    industries={industries}
                    employeerange={employeerange}
                    industrySector={industrySector}
                    relationshipManager={relationshipManager}
                    servicingManager={servicingManager}
                    getOptions={getOptions}
                    getManagerOptions={getManagerOptions}
                    type={type}
                    dataExist={dataExist} // Pass dataExist to FormContent
                  />
                </div>
              )}
            </Formik>
          </WidthLimiter>
        </HomeWrapper>
      )}
    </>
  );
}

export default CompanySetup;

const ButtonWrapper = styled.div`
  max-width: 800px;
`;

const ContactCard = styled.div`
  background-color: #F6F3FF;
  border-radius: 8px;
  padding: 24px;
`;

const ContactHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ContactTitle = styled.h4`
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 0;
`;

const GridTwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;
  margin-top: 12px;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #1A1A1A;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  &:hover {
    color: #440e69;
  }
`;
